import { useState } from "react";

import { makeDraftDollarAmount } from "./DollarAmountsField";
import {
  type ChildAgeBand,
  type DraftChildBand,
  isBlankChildBand,
  isValidChildBand,
  makeEmptyDraftChildBand,
  toChildAgeBand,
  toDraftChildBands,
} from "./childAgeBandValues";

export interface ChildAgeBandsState {
  bands: DraftChildBand[];
  showErrors: boolean;
  updateBand: (index: number, patch: Partial<DraftChildBand>) => void;
  addBand: () => void;
  removeBand: (index: number) => void;
  updateAmount: (index: number, amountIndex: number, value: string) => void;
  addAmount: (index: number) => void;
  removeAmount: (index: number, amountIndex: number) => void;
  /**
   * Filters out untouched blank bands, validates the rest, and returns the
   * stored shape. Returns null and flips `showErrors` on when any filled band
   * is invalid — the caller should not persist in that case.
   */
  getSaveValue: () => ChildAgeBand[] | null;
}

/**
 * Draft state, mutation, and validation for the child benefit age-band editor
 * (`childBenefitAmountsByAge`) — shared by the Present and Build shells. Each
 * shell owns its own AG Grid persistence (stopEditing / setDataValue) and any
 * surface-specific concerns (Present's custom-text mode); this hook only
 * tracks the band list itself, so the editing rules can never drift between
 * the two surfaces.
 */
export function useChildAgeBands(
  initialBands: ChildAgeBand[],
): ChildAgeBandsState {
  const [bands, setBands] = useState<DraftChildBand[]>(() =>
    toDraftChildBands(initialBands),
  );
  const [showErrors, setShowErrors] = useState(false);

  const updateBand = (index: number, patch: Partial<DraftChildBand>): void =>
    setBands((prev) =>
      prev.map((band, i) => (i === index ? { ...band, ...patch } : band)),
    );

  const addBand = (): void =>
    setBands((prev) => [...prev, makeEmptyDraftChildBand()]);

  const removeBand = (index: number): void =>
    setBands((prev) => prev.filter((_, i) => i !== index));

  const updateAmount = (
    index: number,
    amountIndex: number,
    value: string,
  ): void =>
    setBands((prev) =>
      prev.map((band, i) =>
        i === index
          ? {
              ...band,
              amounts: band.amounts.map((entry, j) =>
                j === amountIndex ? { ...entry, amount: value } : entry,
              ),
            }
          : band,
      ),
    );

  const addAmount = (index: number): void =>
    setBands((prev) =>
      prev.map((band, i) =>
        i === index
          ? { ...band, amounts: [...band.amounts, makeDraftDollarAmount()] }
          : band,
      ),
    );

  const removeAmount = (index: number, amountIndex: number): void =>
    setBands((prev) =>
      prev.map((band, i) =>
        i === index
          ? {
              ...band,
              amounts: band.amounts.filter((_, j) => j !== amountIndex),
            }
          : band,
      ),
    );

  const getSaveValue = (): ChildAgeBand[] | null => {
    const filledBands = bands.filter((band) => !isBlankChildBand(band));

    if (!filledBands.every(isValidChildBand)) {
      setShowErrors(true);
      return null;
    }

    return filledBands.map(toChildAgeBand);
  };

  return {
    bands,
    showErrors,
    updateBand,
    addBand,
    removeBand,
    updateAmount,
    addAmount,
    removeAmount,
    getSaveValue,
  };
}
